import { defineMessages } from 'react-intl';
import type { IntlShape, MessageDescriptor } from 'react-intl';
import { DropdownItem } from '@designsystem/stories/02-atoms/Dropdown';

const messages = defineMessages({
  degreeProgramTypeBachelor: {
    id: 'filter_degree_program_type_bachelor',
    defaultMessage: 'Bachelor',
  },
  degreeProgramTypeMaster: {
    id: 'filter_degree_program_type_master',
    defaultMessage: 'Master',
  },
});

type MergedFilterGroup = {
  /** Synthetic value shown in the UI and stored in state/URL. */
  value: string;
  label: MessageDescriptor;
  /** Real vocabulary tokens this group stands for. */
  members: string[];
};

/**
 * Listing-block facet values that are visually merged. The synthetic value
 * (e.g. `bachelor`) lives only in UI state/URL and is expanded to its real
 * member tokens before any backend call (see `expandActiveFilters`).
 */
export const MERGED_FILTER_VALUES: Record<string, MergedFilterGroup[]> = {
  degree_program_type: [
    {
      value: 'bachelor',
      label: messages.degreeProgramTypeBachelor,
      members: ['ba', 'bsc'],
    },
    {
      value: 'master',
      label: messages.degreeProgramTypeMaster,
      members: ['ma', 'msc'],
    },
  ],
};

/**
 * Replace a filter's member checkboxes with one synthetic super-category
 * checkbox. Unmapped indexes pass through; groups with no surviving members
 * (count 0) vanish.
 */
export function collapseFacetForDisplay(
  values: DropdownItem[],
  filterIndex: string,
  intl: IntlShape,
): DropdownItem[] {
  const groups = MERGED_FILTER_VALUES[filterIndex];
  if (!groups) return values;

  const seen = new Set<string>();
  const result: DropdownItem[] = [];

  values.forEach((item) => {
    const group = groups.find((g) => g.members.includes(item.value as string));
    if (!group) {
      result.push(item); // not grouped (e.g. Lehrdiplom) — keep as is
      return;
    }
    if (seen.has(group.value)) return; // group already emitted
    seen.add(group.value);
    result.push({
      type: 'checkbox',
      name: filterIndex,
      value: group.value,
      label: intl.formatMessage(group.label),
    });
  });

  return result;
}

/** Rewrite each filter value of a mapped index, leaving everything else alone. */
function mapActiveFilters<
  T extends { [key: string]: string | string[] } | undefined,
>(
  activeFilters: T,
  rewrite: (token: string, groups: MergedFilterGroup[]) => string[],
): T {
  if (!activeFilters) return activeFilters;

  const result: { [key: string]: string | string[] } = {};
  Object.entries(activeFilters).forEach(([key, value]) => {
    const groups = MERGED_FILTER_VALUES[key];
    if (!groups || value === null || value === undefined) {
      result[key] = value;
      return;
    }
    const tokens = Array.isArray(value) ? value : [value];
    // A group and one of its members can both be present (e.g. a legacy
    // `bsc` next to a freshly ticked `bachelor`) — dedupe, order preserved.
    result[key] = [...new Set(tokens.flatMap((t) => rewrite(t, groups)))];
  });
  return result as T;
}

/**
 * Expand group values to their real member tokens before active filters reach
 * the backend. A member token that is still in the URL (bookmark, shared link,
 * search-engine result) widens to its whole group, so it behaves exactly like
 * the group checkbox the user can see. Idempotent; unmapped values pass through.
 */
export function expandActiveFilters<
  T extends { [key: string]: string | string[] } | undefined,
>(activeFilters: T): T {
  return mapActiveFilters(
    activeFilters,
    (token, groups) =>
      groups.find((g) => g.value === token || g.members.includes(token))
        ?.members ?? [token],
  );
}

/**
 * Fold real member tokens back into their synthetic group value, for the filter
 * UI state only — never for a backend payload. Without this a legacy
 * `?degree_program_type=bsc` leaves every checkbox unticked: the listing is
 * filtered, but nothing shows it and there is no control to clear it.
 *
 * Collapsing and then expanding lands on the same tokens as expanding directly,
 * which is what keeps the SSR subrequest key identical on both sides.
 */
export function collapseActiveFilters<
  T extends { [key: string]: string | string[] } | undefined,
>(activeFilters: T): T {
  return mapActiveFilters(activeFilters, (token, groups) => [
    groups.find((g) => g.members.includes(token))?.value ?? token,
  ]);
}
